import {
  Button,
  Card,
  Col,
  Container,
  Form,
  ListGroup,
  Row,
  Table,
} from 'react-bootstrap'
import styles from './orderDetails.module.css'
import { useNavigate, useParams } from 'react-router-dom'
import { Loader } from '../../../components/common/loader/Loader'
import {
  useAddPaymentMutation,
  useGetOrderByIdQuery,
  useLazyDownloadInvoiceQuery,
  useUpdateDeliveryStatusMutation,
} from '../../../features/api/order/orderApi'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faList, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons'
import { PageHeading } from '../../../components/common/pageHeading/PageHeading'
import dateFormatter from '../../../utils/dateFormatter'
import { OrderLine } from './OrderLine'
import { SampleLine } from './SampleLine'
import * as v from 'valibot'
import { PaymentDetailsSection } from '../../../components/orderData/placeOrder/sections/PaymentDetailsSection'
import { useFieldArray, useForm } from 'react-hook-form'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { PaymentLine } from './PaymentLine'
import { useCallback, useEffect, useState } from 'react'
import useIsMobile from '../../../customHooks/useIsMobile'
import { useIsSalesExecutive } from '../../../customHooks/roleChecker/useIsSalesExecutive'
import { useIsAdmin } from '../../../customHooks/roleChecker/useIsAdmin'
import { useIsSuperAdmin } from '../../../customHooks/roleChecker/useIsSuperAdmin'
import AlertDismissible from '../../../components/common/alertDismissible/AlertDismissible'
import { DeliveryStatus } from '../../../app/constant/DeliveryStatus'
import { useIsSeniorSalesExecutive } from '../../../customHooks/roleChecker/useIsSeniorSalesExecutive'

export const OrderDetails = () => {
  const isMobile = useIsMobile()
  const { orderId } = useParams()
  const navigate = useNavigate()

  const [showAlert, setShowAlert] = useState(false)
  const isSalesExecutive = useIsSalesExecutive()
  const isSeniorSalesExecutive = useIsSeniorSalesExecutive()
  const isAdmin = useIsAdmin()
  const isSuperAdmin = useIsSuperAdmin()
  // State for custom phone input
  const [customPhone, setCustomPhone] = useState('')

  // State for loading send invoice
  const [isSending, setIsSending] = useState(false)

  const [isAmountValid, setIsAmountValid] = useState(true)
  const {
    data: orderDetails,
    isLoading,
    isError,
  } = useGetOrderByIdQuery(orderId)
  const [
    updateDeliveryStatus,
    {
      isLoading: isDeliveryStatusChangeLoading,
      isSuccess: isDeliveryStatusChangeSuccess,
      isError: isDeliveryStatusChangeError,
    },
  ] = useUpdateDeliveryStatusMutation()
  const [
    addPayment,
    { isAddPaymentLoading, isAddPaymentSuccess, isAddPaymentError },
  ] = useAddPaymentMutation()

  const [triggerDownloadInvoice] = useLazyDownloadInvoiceQuery()

  const schema = v.object({
    paymentDetailsRequestDtos: v.optional(
      v.array(
        v.object({
          paymentMethod: v.union(
            [v.literal('CASH'), v.literal('BANK TRANSFER'), v.literal('CHECK')],
            'Select a valid payment method',
          ),
          amountPaid: v.number([
            v.minValue(0.01, 'Amount must be positive'),
            v.pipe((val, ctx) => {
              const allPayments = ctx?.input?.paymentDetailsRequestDtos || []
              const totalPaid = allPayments.reduce(
                (sum, p) => sum + (Number(p.amountPaid) || 0),
                0,
              )
              const amountDue = ctx?.parent?.amountDue ?? Infinity
              if (totalPaid > amountDue) {
                return ctx?.issue('Total exceeds amount due')
              }
              return val
            }),
          ]),
        }),
      ),
    ),
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setError,
    clearErrors,
  } = useForm({
    resolver: valibotResolver(schema),
    mode: 'onChange',
    defaultValues: {
      orderType: '',
      customerId: '',
      orderDetailsRequestDtos: [],
      sampleDetailsRequestDtos: [],
      paymentDetailsRequestDtos: [],
    },
  })

  const paymentFields = useFieldArray({
    control,
    name: 'paymentDetailsRequestDtos',
  })

  const totalQuantity =
    orderDetails?.orderDetailsResponseDtoList?.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0,
    ) || 0

  const onSubmit = async (data) => {
    setIsAmountValid(true)
    const hasPaymentDetails = data.paymentDetailsRequestDtos?.length > 0
    if (!hasPaymentDetails) {
      return
    }
    const isValid = checkPaymentAmount(data.paymentDetailsRequestDtos)
    if (!isValid) {
      setIsAmountValid(false)
      return
    }
    try {
      await addPayment({
        data: data.paymentDetailsRequestDtos,
        orderId,
      }).unwrap()
      // handleSuccess()
    } catch (err) {
    } finally {
      reset()
    }
  }

  const checkPaymentAmount = useCallback(
    (payments = []) => {
      const totalPaid = payments.reduce(
        (sum, p) => sum + (Number(p.amountPaid) || 0),
        0,
      )
      return totalPaid > 0 && totalPaid <= (orderDetails?.amountDue ?? 0)
    },
    [orderDetails?.amountDue],
  )

  const onInvoiceDownload = async () => {
    try {
      const res = await triggerDownloadInvoice(orderId).unwrap()

      if (!res?.document) throw new Error('No document found')

      const byteCharacters = atob(res.document)
      const byteNumbers = Array.from(byteCharacters).map((char) =>
        char.charCodeAt(0),
      )
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/pdf' })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.setAttribute(
        'download',
        `${orderDetails?.orderType.substring(0, 3)}_${
          orderDetails.companyName
        }_${dateFormatter(orderDetails?.createdAt)}.pdf`,
      )
      link.href = url
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download invoice:', err)
    }
  }

  // Function to send invoice to phone (calls backend)
  const sendInvoiceToPhone = async (phoneNumber) => {
    if (!phoneNumber) return alert('Please provide a phone number')

    setIsSending(true)
    try {
      const response = await fetch(
        `/api/order/${orderId}/sendInvoice?phoneNumber=${encodeURIComponent(
          phoneNumber,
        )}`,
        {
          method: 'POST',
        },
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || 'Failed to send invoice')
      }

      alert('Invoice sent successfully!')
    } catch (err) {
      alert('Error sending invoice: ' + err.message)
    } finally {
      setIsSending(false)
    }
  }
  //Confirm deliver -
  const handleConfirmDelivery = () => {
    setShowAlert(true)
  }
  const handleConfirm = async (confirmed) => {
    setShowAlert(confirmed)
    if (confirmed) {
      await updateDeliveryStatus({
        orderId,
        status: DeliveryStatus.Complete,
      }).unwrap()
      setTimeout(() => {
        navigate('/order')
      }, 1000)
    }
    return
  }
  //Decide what to render -

  let content = null

  if (isLoading) {
    content = (
      <div className='mb-5'>
        <PageHeading title={'Order details'} />
        <Loader />
      </div>
    )
  }

  if (orderDetails) {
    content = (
      <div className='mb-5'>
        <AlertDismissible
          title='Warning!'
          msg='Are you sure you want to proceed?'
          type='warning'
          showAlert={showAlert}
          onConfirm={handleConfirm}
        />
        <PageHeading title={'Order details'} />
        <Container className={`${styles.orderContainer} my-5`}>
          <Button
            variant='outline-light'
            onClick={() => navigate('/order')}
            className={`${styles.btnOrderList} m-4`}
          >
            <FontAwesomeIcon icon={faList} size={isMobile ? 'lg' : 'xl'} />
          </Button>{' '}
          <h2 className={`${styles.customerName} shadow-sm`}>
            {orderDetails?.companyName}
          </h2>
          {
            <Button
              variant='light'
              onClick={onInvoiceDownload}
              className={`${styles.btnReceiptDownload} ${
                isMobile ? 'm-3' : 'm-4'
              }`}
            >
              <FontAwesomeIcon icon={faFileInvoiceDollar} size='xl' />
            </Button>
          }{' '}
          {/* send invoice  */}
          <hr />
          <Row>
            <Col lg={6} md={6} xs={12} className='mt-1 d-flex'>
              <Card border='info' className='w-100 h-100'>
                <Card.Header>Order information</Card.Header>
                <Card.Body>
                  <Card.Title className={styles.cardTitle}>
                    {orderDetails?.reference}
                  </Card.Title>
                  <Card.Subtitle className='mb-1 bg-warning text-dark px-2 py-1'>
                    {orderDetails?.orderType} | {orderDetails?.paymentStatus}
                  </Card.Subtitle>
                  <hr />
                  <Card.Subtitle className='mb-1 bg-warning text-dark px-2 py-1 d-flex justify-content-between align-items-center'>
                    <span>STATUS | {orderDetails?.delStatus}</span>
                    {orderDetails?.delStatus === DeliveryStatus.Pending &&
                      orderDetails?.orderType !== 'DRAFT' && (
                        <Button
                          variant='dark'
                          size='sm'
                          onClick={handleConfirmDelivery}
                        >
                          Delivered
                        </Button>
                      )}
                  </Card.Subtitle>

                  <Card.Text className='px-2'>
                    <small>
                      Order date : {dateFormatter(orderDetails?.createdAt)}
                    </small>
                    <small className={styles.createdBy}>
                      {' '}
                      | By : {orderDetails?.createdBy}
                    </small>
                  </Card.Text>
                  <Card.Text className='px-2'>
                    <small>
                      Delivery date : {dateFormatter(orderDetails?.deliveredAt)}
                    </small>
                    <small className={styles.createdBy}>
                      {' '}
                      | By : {orderDetails?.delBy}
                    </small>
                  </Card.Text>
                  <ListGroup variant='flush'>
                    <ListGroup.Item>
                      {' '}
                      Total HT : {orderDetails?.totalHtBeforeReduction}€
                    </ListGroup.Item>
                    <ListGroup.Item>
                      {' '}
                      Reduction : {orderDetails?.reductionHt}€
                    </ListGroup.Item>
                    <ListGroup.Item>
                      {' '}
                      Total HT after reduction : {orderDetails?.total}€
                    </ListGroup.Item>
                    <ListGroup.Item>
                      {' '}
                      Total VAT : {orderDetails?.vat}€
                    </ListGroup.Item>
                    <ListGroup.Item>
                      Total TTC : {orderDetails?.totalTTC}€
                    </ListGroup.Item>
                    <ListGroup.Item>
                      Paid : {orderDetails?.paid}€
                    </ListGroup.Item>
                    <ListGroup.Item className='text-danger fw-bold'>
                      {' '}
                      Due : {orderDetails?.amountDue}€
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6} md={6} xs={12} className='mt-1 d-flex'>
              <Card border='info' className='w-100 h-100'>
                <Card.Header>Contact</Card.Header>
                <Card.Body>
                  <Card.Title className={styles.cardTitle}>
                    {orderDetails?.lastName} {orderDetails?.firstName}
                  </Card.Title>
                  <Card.Subtitle className='mb-1 text-dark px-2 py-1'>
                    Registration : {orderDetails?.registration}
                  </Card.Subtitle>
                  <Card.Text className='px-2'>
                    <small>Address : {orderDetails?.address}</small>
                  </Card.Text>
                  <Card.Text className='px-2 bg-light py-2'>
                    <small>
                      Delivery Address : {orderDetails?.deliveryAddress}
                    </small>
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className='my-1'>
            <Col className='mt-1'>
              <Card border='info'>
                <Card.Header>
                  Product details | {totalQuantity} Box in total
                </Card.Header>
                <Card.Body>
                  <Table responsive striped bordered hover size='sm'>
                    <thead>
                      <tr className='text-center'>
                        <th>No</th>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Price(€)</th>
                        <th>Size</th>
                        <th>VAT Cat</th>
                        <th>VAT/U(€)</th>
                        <th>Sub total HT(€)</th>
                        <th>Sub total TTC(€)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderDetails?.orderDetailsResponseDtoList?.map(
                        (product, index) => (
                          <OrderLine
                            item={product}
                            number={index + 1}
                            key={index}
                          />
                        ),
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className='my-1'>
            <Col className='mt-1'>
              <Card border='info'>
                <Card.Header>VAt details</Card.Header>
                <Card.Body>
                  <Table responsive striped bordered hover size='sm'>
                    <thead>
                      <tr className='text-center'>
                        <th>Vat category</th>
                        <th>Vat applied</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className='text-center'>
                        <td>A</td>
                        <td>5.5%</td>
                      </tr>
                      <tr className='text-center'>
                        <td>B</td>
                        <td>10%</td>
                      </tr>
                      <tr className='text-center'>
                        <td>C</td>
                        <td>20%</td>
                      </tr>
                      <tr className='text-center'>
                        <td>D</td>
                        <td>0%</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          {orderDetails?.sampleDetailResponseDtoList?.length > 0 && (
            <Row className='my-1'>
              <Col className='mt-1'>
                <Card border='info'>
                  <Card.Header>Sample details</Card.Header>
                  <Card.Body>
                    <Table responsive striped bordered hover size='sm'>
                      <thead>
                        <tr className='text-center'>
                          <th>Product</th>
                          <th>Qty</th>
                          <th>Size</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetails?.sampleDetailResponseDtoList?.map(
                          (product, index) => (
                            <SampleLine item={product} key={index} />
                          ),
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
          {orderDetails?.paymentDetailsResponseDtoList?.length > 0 && (
            <Row className='my-1'>
              <Col className='mt-1'>
                <Card border='info'>
                  <Card.Header>Payment details</Card.Header>
                  <Card.Body>
                    <Table responsive striped bordered hover size='sm'>
                      <thead>
                        <tr className='text-center'>
                          <th>Amount</th>
                          <th>Method</th>
                          <th>Date</th>
                          <th>By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetails?.paymentDetailsResponseDtoList?.map(
                          (payment, index) => (
                            <PaymentLine payment={payment} key={index} />
                          ),
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
          {!isSalesExecutive &&
            !isSeniorSalesExecutive &&
            orderDetails?.paymentStatus !== 'PAID' &&
            orderDetails?.orderType !== 'DRAFT' && (
              <Row className='my-2'>
                <Col className='mt-1'>
                  <Card border='info'>
                    <Card.Header>Action</Card.Header>
                    <Row className='p-2'>
                      <Col xs={12}>
                        <Row>
                          <h4>
                            <span>Payment </span>
                            Info
                          </h4>
                        </Row>
                        <hr />
                        <Form onSubmit={handleSubmit(onSubmit)}>
                          <PaymentDetailsSection
                            orderType={orderDetails?.orderType}
                            register={register}
                            errors={errors}
                            paymentFields={paymentFields}
                          />
                          <hr />
                          <Row className='justify-content-center'>
                            <Col md={12} className='text-center'>
                              {!isAmountValid && (
                                <p className='text-danger text-center animate__animated animate__flipInX'>
                                  Invalid amount
                                </p>
                              )}

                              <Button type='submit' variant='success'>
                                Confirm
                              </Button>
                            </Col>
                          </Row>
                        </Form>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            )}
        </Container>
      </div>
    )
  }
  return content
}
